import { catchError, map, take, takeUntil } from "rxjs/operators";
import { ToActions } from "../../to.actions";
import { ToScreenService } from "../../../pb-screen/services/to-screen.service";
import { TransportOrder } from "../../../pb-screen/models/transport-order/transport-order.interface";
import { select } from "@angular-redux/store";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of, Subject } from "rxjs";
import { CapacitySlot } from "../../../pb-screen/models/capacity-slot.interface";
import { PlanningConflict } from "../../../pb-screen/models/planning-conflict.interface";
import { PlanningConflictType } from "../../../pb-screen/models/planning-conflict-type.enum";
import { SharedService } from "../../../shared/services/shared.service";
import { Haulier } from "../../../pb-screen/models/haulier.interface";
import { Driver } from "../../../static/models/driver.interface";
import { SharedActions } from "../../../shared/shared.actions";
import { IdNameAsStringObject } from "../../../pb-screen/models/quick-view/id-name-as-string-object.interface";
import { ToCapacitySelectorComponent } from "../to-capacity-selector/to-capacity-selector.component";
import { SimpleModalComponent } from "ngx-simple-modal";
import { PlanningBoardActions } from "../../../planningBoard/planningBoard.action";
import { UserFeedbackService } from "../../../services/user-feedback.service";
import { DevLoggerService } from "../../../shared/services/dev-logger.service";
import { PlanningBoardFacade } from "../../../planningBoard/services/planning-board.facade";
import { environment } from "../../../../environments/environment";

@Component({
  moduleId: module.id,
  selector: "to-planning-modal",
  templateUrl: "./to-planning-modal.component.html",
})

export class ToPlanningModalComponent extends SimpleModalComponent<null, null> implements OnInit, OnDestroy {

  @select("selectedTransportOrders") public selectedTransportOrders$: Observable<TransportOrder[]>;
  @ViewChild(ToCapacitySelectorComponent) public capacitySelectorComponent: ToCapacitySelectorComponent;
  @select("capacitySlots") public capacitySlots$: Observable<CapacitySlot[]>;
  @select("drivers") public drivers$: Observable<Driver[]>;
  @select("loading") public loading$: Observable<boolean>;


  public capacitySlots: CapacitySlot[] = [];
  public selectedTransportOrders: TransportOrder[] = [];
  public drivers: Driver[] = [];
  public selectedPlanningGroup: IdNameAsStringObject;
  public selectedHaulier: Haulier;
  public selectedDriver: Driver;
  public selectedCoDriver: Driver;
  public loadingSpinner: boolean = false;
  public selectedCapacity: CapacitySlot;
  public selectedTransportOrder: TransportOrder = null;
  public assignedDriver: string = "";
  public assignedTruck: string = "";
  destroy$ = new Subject();

  // Optimistic UI feature flag
  public useOptimisticPlanning = environment.enableOptimisticPlanning;

  constructor(
    public readonly toScreenService: ToScreenService,
    public readonly toActions: ToActions,
    public readonly userFeedbackService: UserFeedbackService,
    private readonly translate: TranslateService,
    public readonly sharedService: SharedService,
    public readonly sharedActions: SharedActions,
    public readonly planningBoardActions: PlanningBoardActions,
    private readonly devLogger: DevLoggerService,
    private readonly planningBoardFacade: PlanningBoardFacade
  ) {
    super();
  }

  public ngOnInit(): void {

    this.loading$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (!data) {
        this.loadingSpinner = data;
      }
    });

    this.drivers$.pipe(takeUntil(this.destroy$)).subscribe((drivers: Driver[]) => {
      if (drivers) {
        this.drivers = drivers;
      }
      if (this.drivers.length > 0) {

        if (this.assignedDriver.length > 0) {
          let toDriverList = this.drivers.filter(d => d.id === this.assignedDriver);
          if (toDriverList.length > 0) {
            this.selectedDriver = toDriverList[0];
          }
        } else {
          this.selectedDriver = null;
        }

        if (this.selectedTransportOrder) {
          let toCoDriverList = this.drivers.filter(d => d.id === this.selectedTransportOrder.firstCoDriver);
          if (toCoDriverList.length > 0) {
            this.selectedCoDriver = toCoDriverList[0];
          }
        } else {
          this.selectedCoDriver = null;
        }
      }
    });

    this.selectedTransportOrders$.pipe(takeUntil(this.destroy$)).subscribe((tos: TransportOrder[]) => {
      this.clearModalSelection();
      this.selectedTransportOrders = tos;
      this.selectedTransportOrder = tos[0];

      if (this.selectedTransportOrders.length === 1) {
        this.assignedDriver = this.selectedTransportOrder.firstDriver;
        this.assignedTruck = this.selectedTransportOrder.truckNo;

        let selectedCapacity = this.capacitySlots.filter((capacitySlot: CapacitySlot) => {
          return capacitySlot.truck.id === this.assignedTruck;
        })[0];

        if (selectedCapacity) {
          this.selectedCapacity = selectedCapacity;
        } else {
          this.selectedCapacity = null;
        }

        if (this.drivers && this.drivers.length > 0) {
          let toDriverList = this.drivers.filter(d => this.assignedDriver && d.id === this.assignedDriver);
          if (toDriverList.length > 0) {
            this.selectedDriver = toDriverList[0];
          }

          let toCoDriverList = this.drivers.filter(d => this.selectedTransportOrder && d.id === this.selectedTransportOrder.firstCoDriver);
          if (toCoDriverList.length > 0) {
            this.selectedCoDriver = toCoDriverList[0];
          }
        } else {
          this.selectedDriver = null;
          this.selectedCoDriver = null;
        }
      }
    });


    this.capacitySlots$.pipe(takeUntil(this.destroy$)).subscribe((capacitySlots: CapacitySlot[]) => {
      this.capacitySlots = capacitySlots;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getSelectedPlanningGroup(planningGroup: IdNameAsStringObject) {
    this.selectedPlanningGroup = planningGroup;
  }

  public getSelectedCapacity(capacity: CapacitySlot) {
    this.selectedCapacity = capacity;
  }


  public getSelectedHaulier(haulier: Haulier) {
    this.selectedHaulier = haulier;
  }

  public getSelectedDriver(driver: Driver) {
    this.selectedDriver = driver;
  }

  public getSelectedCoDriver(driver: Driver) {
    this.selectedCoDriver = driver;
  }

  public assignCapacity(): void {
    // Check if we should use optimistic planning flow
    if (this.useOptimisticPlanning && this.selectedCapacity) {
      this.devLogger.planning('ToPlanningModal: Using optimistic planning flow');
      this.executeOptimisticPlanning();
      return;
    }

    // Original flow
    this.toScreenService.assignCapacityToTO(this.selectedTransportOrders[0],
      this.selectedCapacity,
      this.selectedPlanningGroup ? this.selectedPlanningGroup.id : "",
      this.selectedHaulier ? this.selectedHaulier.id : "",
      this.selectedDriver ? this.selectedDriver.id : " ",
      this.selectedCoDriver ? this.selectedCoDriver.id : " ")
      .subscribe((to: TransportOrder) => {
        this.sharedActions.updateTransportOrdersDisplay([to]);
        this.toActions.setSelectedTransportOrders([to]);
        this.planningBoardActions.UpdatePlanningDisplay([to]);
        this.userFeedbackService.showSuccessMessage("SHARED.CAPACITY_ASSIGNED");
        this.calculateAndDisplayPlanningConflicts(to.id).subscribe();
        this.closeModal();
      }, _ => {
        this.loadingSpinner = false;
      });
  }

  /**
   * Execute planning using optimistic UI flow
   * Shows immediate feedback while backend processes
   */
  private executeOptimisticPlanning(): void {
    const to = this.selectedTransportOrders[0];
    const planningBlocks = to.planningBlocks || [];
    
    this.devLogger.planning('ToPlanningModal: executeOptimisticPlanning', {
      toId: to.id,
      planningBlocksCount: planningBlocks.length,
      capacitySlot: this.selectedCapacity?.truck?.name
    });

    // Get depot in/out planning blocks from the TO
    const depotInBlocks = planningBlocks.filter((pb: any) => pb.planningBlockType === 'DepotIn' || pb.pbType === 'IN');
    const depotOutBlocks = planningBlocks.filter((pb: any) => pb.planningBlockType === 'DepotOut' || pb.pbType === 'OUT');

    try {
      // Execute optimistic planning through the facade (synchronous - returns immediately)
      const response = this.planningBoardFacade.executePlanningOptimistic(
        depotInBlocks as any,
        depotOutBlocks as any,
        this.selectedCapacity as any, // Cast for type compatibility
        new Date(), // planDate
        '', // userId - will be filled by facade
        {
          existingTransportOrder: to as any,
          selectedDriver: this.selectedDriver
        }
      );

      this.devLogger.planning('ToPlanningModal: Optimistic planning initiated', {
        optimisticTOId: response.optimisticTOId,
        accepted: response.accepted
      });
      
      // Show info message
      this.userFeedbackService.showInfoMessageWithoutTranslating(
        'Planning in progress - you can continue working',
        'Optimistic Planning'
      );
      
      // Close modal immediately for better UX
      this.closeModal();
    } catch (err) {
      this.devLogger.error('ToPlanningModal', 'Optimistic planning failed', err);
      this.loadingSpinner = false;
      this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.PLANNING_ERROR");
    }
  }

  public calculateAndDisplayPlanningConflicts(transportOrderNumber: string): Observable<any> {
    this.devLogger.conflict(`calculateAndDisplayPlanningConflicts called for TO: ${transportOrderNumber}`);
    const conflictMessageConfig = {
      timeOut: 10000,
      extendedTimeOut: 10000,
      closeButton: true
    };

    return this.sharedService.calculatePlanningBlockConflicts(transportOrderNumber).pipe(
      map((conflicts: PlanningConflict[]) => {
        this.devLogger.conflict(`Received ${conflicts.length} conflicts`, conflicts);
        const uniqueConflicts = [...conflicts.reduce((accu, current) => {
          accu.set(current.conflictMessage || current.conflictReason || current.ressourceNo, current);
          return accu;
        }, new Map()).values()];

        this.devLogger.conflict(`${uniqueConflicts.length} unique conflicts to display`);
        this.translate.get("MESSAGE.PLANNING_CONFLICT")
          .pipe(take(1))
          .subscribe(conflictTitle => {
            uniqueConflicts.forEach((planningConflict: PlanningConflict) => {
              const formattedConflictMessage = planningConflict.conflictMessage != "" ? planningConflict.conflictMessage : planningConflict.conflictReason;
              this.devLogger.conflict(`Showing toast: ${planningConflict.conflictType} - ${formattedConflictMessage}`);

              switch (planningConflict.conflictType) {
                case PlanningConflictType.WARNING:
                  this.userFeedbackService.showWarningMessageWithoutTranslating(
                    formattedConflictMessage,
                    conflictTitle,
                    conflictMessageConfig
                  );
                  break;
                case PlanningConflictType.ERROR:
                  this.userFeedbackService.showErrorMessageWithoutTranslating(
                    formattedConflictMessage,
                    conflictTitle,
                    conflictMessageConfig
                  );
                  break;
                default:
                  this.userFeedbackService.showInfoMessageWithoutTranslating(
                    formattedConflictMessage,
                    conflictTitle,
                    conflictMessageConfig
                  );
                  break;
              }
            });
          });
      }),
      catchError(err => {
        this.devLogger.error('ToPlanningModal', 'Error calculating conflicts', err);
        this.loadingSpinner = false;
        this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.CALCULATING_CONFLICT_ERROR");
        return of([]);
      }), );
  }

  public closeModal(): void {
    this.close();
  }


  clearModalSelection() {
    this.selectedCapacity = null;
    if (this.capacitySelectorComponent) {
      this.capacitySelectorComponent.selectedCapacity = null;
    }

    this.selectedHaulier = null;
    if (this.capacitySelectorComponent) {
      this.capacitySelectorComponent.selectedHaulier = null;
    }

    this.selectedDriver = null;
    if (this.capacitySelectorComponent) {
      this.capacitySelectorComponent.selectedDriver = null;
    }

    this.selectedCoDriver = null;
    if (this.capacitySelectorComponent) {
      this.capacitySelectorComponent.selectedCoDriver = null;
    }
  }
}
